import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faLock } from "@fortawesome/free-solid-svg-icons";
import http from "../../../http-common";

export default function WaitlistPanel({ epkId, onClose }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const token = user?.token;

  // Configuration States
  const [closedAt, setClosedAt] = useState(null);

  // Entries State
  const [entries, setEntries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [epkRes, entriesRes] = await Promise.all([
        http.get(`/fepks/${epkId}`),
        http.get(`/waitlist/${epkId}/entries`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const config = epkRes.data?.waitlistConfig || {};
      setClosedAt(config.closedAt || null);

      setEntries(entriesRes.data?.entries || []);
    } catch (err) {
      toast.error(t("Failed to load waitlist configuration"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (epkId && token) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epkId, token]);

  const handleCloseWaitlist = async () => {
    setClosing(true);
    try {
      await http.post(
        `/waitlist/${epkId}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("Waitlist campaign closed successfully"));
      setShowConfirmClose(false);
      loadData(); // reload all data to capture positionAtClose snapshot
    } catch (err) {
      toast.error(err.response?.data?.message || t("Failed to close waitlist"));
    } finally {
      setClosing(false);
    }
  };

  const sanitizeCsvCell = (val) => {
    const s = String(val ?? "");
    return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  };

  const exportCSV = () => {
    if (!entries || entries.length === 0) {
      toast.info(t("No entries to export"));
      return;
    }
    const headers = [
      t("Position"),
      t("First Name"),
      t("Last Name"),
      t("Email"),
      t("Referral Count"),
      t("Email Verified"),
      t("Joined Date"),
    ];
    const rows = entries.map((e) => [
      e.position || "-",
      e.firstName,
      e.lastName,
      e.email,
      e.referralCount || 0,
      e.emailVerified ? t("Yes") : t("No"),
      new Date(e.createdAt).toLocaleDateString(),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((val) => `"${sanitizeCsvCell(val).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `waitlist_entries_${epkId}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-p-4">
      <div className="tw-w-full tw-max-w-4xl tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-2xl tw-text-gray-800 tw-flex tw-flex-col tw-max-h-[90vh]">
        
        {/* Title & Close */}
        <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
          <h2 className="tw-m-0 tw-text-xl tw-font-semibold tw-text-[#1E0039]">
            {t("Waitlist Entries")}
          </h2>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-lg"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="tw-py-12 tw-text-center tw-flex-1">
            <div className="tw-mx-auto tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-4 tw-border-[#1E0039] tw-border-t-transparent tw-mb-2" />
            <p className="tw-text-sm tw-text-gray-500">{t("Loading waitlist data...")}</p>
          </div>
        ) : (
          <>
            {/* Header / Export / Close Button */}
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <p className="tw-m-0 tw-text-sm tw-text-gray-500">
                {t("Viewing {{count}} entries for this film waitlist.", { count: entries.length })}
              </p>
              <div className="tw-flex tw-gap-3">
                {closedAt ? (
                  // A closed campaign can't be reopened, so the entries view has to say
                  // so explicitly rather than just dropping the Close button silently.
                  <div className="tw-flex tw-items-center tw-gap-2 tw-text-red-600 tw-text-xs tw-font-semibold">
                    <FontAwesomeIcon icon={faLock} />
                    <span>{t("Waitlist Campaign Closed on {{date}}", { date: new Date(closedAt).toLocaleDateString() })}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowConfirmClose(true)}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-border-none tw-cursor-pointer tw-transition-colors"
                  >
                    {t("Close Waitlist Campaign")}
                  </button>
                )}
                <button
                  onClick={exportCSV}
                  className="tw-flex tw-items-center tw-gap-1.5 tw-bg-[#1E0039] hover:tw-bg-[#2d0059] tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-border-none tw-cursor-pointer tw-transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  {t("Export CSV")}
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="tw-flex-1 tw-overflow-y-auto tw-pr-1">
              <div className="tw-space-y-4">
                {/* Table */}
                {entries.length === 0 ? (
                  <p className="tw-text-sm tw-text-gray-400 tw-text-center tw-py-12 tw-bg-gray-50 tw-rounded-lg">
                    {t("No waitlist entries yet.")}
                  </p>
                ) : (
                  <div className="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                    <table className="tw-w-full tw-min-w-[600px] tw-border-collapse tw-text-left tw-text-xs">
                      <thead>
                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-gray-500 tw-font-semibold">
                          <th className="tw-p-3">{t("Rank")}</th>
                          <th className="tw-p-3">{t("Name")}</th>
                          <th className="tw-p-3">{t("Email")}</th>
                          <th className="tw-p-3">{t("Referrals")}</th>
                          <th className="tw-p-3">{t("Verified")}</th>
                          <th className="tw-p-3">{t("Joined Date")}</th>
                        </tr>
                      </thead>
                      <tbody className="tw-divide-y tw-divide-gray-150">
                        {entries.map((entry, idx) => (
                          <tr key={entry._id} className="hover:tw-bg-gray-50">
                            <td className="tw-p-3 tw-font-semibold tw-text-[#FF43A7]">
                              {entry.position ? `#${entry.position}` : "-"}
                            </td>
                            <td className="tw-p-3 tw-font-medium">
                              {entry.firstName} {entry.lastName}
                            </td>
                            <td className="tw-p-3 tw-text-gray-500">{entry.email}</td>
                            <td className="tw-p-3 tw-font-semibold">{entry.referralCount || 0}</td>
                            <td className="tw-p-3">
                              <span
                                className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${
                                  entry.emailVerified
                                    ? "tw-bg-green-50 tw-text-green-600"
                                    : "tw-bg-amber-50 tw-text-amber-600"
                                }`}
                              >
                                {entry.emailVerified ? t("Yes") : t("No")}
                              </span>
                            </td>
                            <td className="tw-p-3 tw-text-gray-400">
                              {new Date(entry.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Actions Footer */}
                <div className="tw-border-t tw-border-gray-100 tw-pt-4 tw-flex tw-justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="tw-rounded-lg tw-border tw-border-gray-300 tw-bg-transparent tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-50 tw-cursor-pointer"
                  >
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal for closing waitlist */}
      {showConfirmClose && (
        <div className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-bg-black/75 tw-p-4">
          <div className="tw-w-full tw-max-w-sm tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-2xl tw-text-gray-800">
            <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-2">
              {t("Confirm Close Campaign")}
            </h3>
            <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed tw-mb-5">
              {t("Are you sure you want to close this waitlist? This will lock the ranking and stamp final positions on all entries. New joins will be disabled.")}
            </p>
            <div className="tw-flex tw-gap-3">
              <button
                onClick={() => setShowConfirmClose(false)}
                className="tw-flex-1 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-transparent tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-600 hover:tw-bg-gray-50 tw-cursor-pointer"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleCloseWaitlist}
                disabled={closing}
                className="tw-flex-1 tw-rounded-lg tw-bg-red-500 hover:tw-bg-red-600 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-white tw-border-none tw-cursor-pointer disabled:tw-opacity-50"
              >
                {closing ? t("Closing...") : t("Confirm Close")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

